import React from "react";
import { ActionsScreenContext } from "./common";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { StackScreenProps } from "@react-navigation/stack";
import { ProductAiInsightsActionsRootScreenParamList } from "../../../../types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  Button,
  Page,
  Stack,
  TextField,
  useFlashMessages,
  View,
} from "@towersystems/roam-common-native";
import { useMutation } from "@apollo/client";
import { MUTATION_EMAIL_QUICK_WIN_RESULT } from "../../../../utilities/llm-quick-wins";
import { Colors } from "react-native-ui-lib";

export interface ProductAiInsightsActionsEnterEmailScreenProps
  extends StackScreenProps<
    ProductAiInsightsActionsRootScreenParamList,
    "ProductAiInsightsActionsEnterEmailScreen"
  > {}

export const ProductAiInsightsActionsEnterEmailScreen = ({
  navigation,
}: ProductAiInsightsActionsEnterEmailScreenProps) => {
  const [performingAction, setPerformingAction] = React.useState(false);
  const context = React.useContext(ActionsScreenContext);
  const transitionEnded = useHasTransitionEnded(navigation);
  const flashMessagesContext = useFlashMessages();
  const [emailAddressInput, setEmailAddressInput] = React.useState("");

  const [m] = useMutation(MUTATION_EMAIL_QUICK_WIN_RESULT);

  const isValid = React.useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailAddressInput);
  }, [emailAddressInput]);

  const handleOnChangeText = React.useCallback(
    (t: string) => {
      setEmailAddressInput(t);
    },
    [setEmailAddressInput],
  );

  const handleOnSubmit = React.useCallback(async () => {
    if (!context?.result?.id) {
      return;
    }
    setPerformingAction(true);
    m({
      variables: {
        id: context.result.id,
        email: emailAddressInput,
      },
    })
      .then(() => {
        flashMessagesContext.showMessage({
          severity: "success",
          message: "Email sent successfully",
        });
        navigation.pop();
      })
      .catch(() => {
        flashMessagesContext.showMessage({
          severity: "error",
          message: "Failed to send email",
        });
      })
      .finally(() => {
        setPerformingAction(false);
      });
  }, [
    context?.result?.id,
    emailAddressInput,
    m,
    flashMessagesContext,
    navigation,
  ]);

  if (!context || context.result === undefined || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const footerMarkup = (
    <Button
      disabled={!isValid || performingAction}
      label={"Send Email"}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleOnSubmit}
    ></Button>
  );

  return (
    <Page
      title="Email AI Summary"
      secondaryTitle="Enter the email address to send the AI summary to"
      footer={footerMarkup}
    >
      <View>
        <TextField
          placeholder={"Enter email address"}
          validate={["required", "email"]}
          value={emailAddressInput}
          onChangeText={handleOnChangeText}
          autoFocus={transitionEnded}
        />
      </View>
    </Page>
  );
};
